import { Injectable, Injector, inject } from "@angular/core";
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
} from "@angular/common/http";
import { Router } from "@angular/router";

import { Observable, throwError } from "rxjs";
import { catchError, finalize, switchMap, take } from "rxjs/operators";

import { LoaderService } from "./loader.service";
import { ToasterService } from "./toaster.service";
import { TranslateService } from "@ngx-translate/core";
import { DEFAULT_LANG, nativeAppRegexes } from "../consts";
import { AngularFireAuth } from "@angular/fire/compat/auth";
import { AuthService } from "./auth.service";
import { AccountDeletionService } from "./account-detetion.service";
import { NavController } from "@ionic/angular";
import { isGloballyHandledError } from "../utils/error.util";

import { environment } from "src/environments/environment";
import { PlatformService } from "./platform.service";
import { IntegrationTestRuntimeService } from "src/app/testing/integration-test-runtime";

@Injectable({
  providedIn: "root",
})
export class RequestInterceptorService implements HttpInterceptor {
  private readonly loaderService = inject(LoaderService);
  private readonly toasterService = inject(ToasterService);
  private readonly angularFireAuth = inject(AngularFireAuth);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);
  private readonly accountDeletionService = inject(AccountDeletionService);
  private readonly platformService = inject(PlatformService);
  private readonly integrationTestRuntime = inject(
    IntegrationTestRuntimeService
  );

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const translateService = this.injector.get(TranslateService); // Interceptor depends on translate service. But the TranslateService might not be available during initialization

    const shouldShowLoading = !req.headers.get("hideLoading");

    if (shouldShowLoading) {
      this.loaderService.show();
    }

    if (req.body && req.body.userId) {
      delete req.body.userId;
    }

    if (nativeAppRegexes.some((reg) => reg.test(req.url?.split("?")?.[0]))) {
      if (req.body && typeof req.body === "object") {
        const modifiedBody = {
          ...req.body,
          fromNativeApp: this.platformService.isAndroid(),
        };
        req = req.clone({ body: modifiedBody });
      } else {
        // For GET requests or when there is no body, add it as a query parameter.
        req = req.clone({
          params: req.params.set(
            "fromNativeApp",
            this.platformService.isAndroid() ? "true" : "false"
          ),
        });
      }
    }

    const idToken$ = this.integrationTestRuntime.isEnabled()
      ? this.integrationTestRuntime.idToken$()
      : this.angularFireAuth.idToken;

    return idToken$.pipe(
      take(1),
      switchMap((idToken) => {
        // Build new request headers
        const headers: { [header: string]: string } = {
          lang: translateService.currentLang ?? DEFAULT_LANG,
        };
        if (idToken) {
          headers["Authorization"] = `Bearer ${idToken}`;
        }

        // Clone the request with updated URL (if changed) and headers
        const clone = req.clone({
          setHeaders: headers,
          withCredentials: true,
        });

        return next.handle(clone).pipe(
          catchError((err) => {
            if (!isGloballyHandledError(err)) {
              this.handleError(err, !!req.headers.get("hideError"));
            }

            return throwError(() => err);
          })
        );
      }),
      finalize(() => {
        if (shouldShowLoading) {
          this.loaderService.hide();
        }
      })
    );
  }

  private async handleError(
    errResponse: HttpErrorResponse,
    hideErrorToast = false
  ): Promise<void> {
    const errorMessage = await this.getErrorMessage(errResponse);
    if (errResponse.status !== 401 && !hideErrorToast) {
      this.toasterService.error(errorMessage);
    }
  }

  private async getErrorMessage(
    errResponse: HttpErrorResponse
  ): Promise<string> {
    let errorMessage = "Something went wrong";
    const translateService = this.injector.get(TranslateService);

    if (errResponse.error instanceof ErrorEvent) {
      // client-side error
      errorMessage = `Error: ${errResponse.error.message}`;
    } else {
      // server-side error
      errorMessage = errResponse?.error?.message || "Something went wrong";
      //on unauthorize logout the user
      if (errResponse.status === 401) {
        this.authService
          .logout()
          .pipe(take(1))
          .subscribe(() => this.router.navigate([`/login`]));
      }

      // too many requests
      if (errResponse.status === 429) {
        errorMessage = translateService
          ? translateService.instant("TOO_MANY_REQUESTS")
          : errResponse.error;
      }

      // user is deleted
      if (
        errResponse.error?.status === 404 &&
        errResponse.error?.message === "Your account has been deleted"
      ) {
        errorMessage = translateService
          ? translateService.instant("ACCOUNT.DELETED")
          : "เราได้ลบบัญชีของคุณแล้ว";

        this.accountDeletionService.showAccountDeletedPopover(
          errResponse.error?.country
        );

        this.authService
          .logout()
          .pipe(take(1))
          .subscribe(() => this.router.navigate([`/`]));
      }

      if (errResponse.error?.status === 400) {
        errorMessage = errResponse.error.message;
      }

      if (errResponse?.error?.message?.includes("EASILY_GUESSED_PIN")) {
        errorMessage = translateService.instant(errResponse.error.message);
      }
    }

    return errorMessage;
  }
}
